import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ThinTextField,
  AddButton,
  ThinDropdownWithSearch,
} from "../../../components/common/GeneralComponent";
import { fetchData, submitFormWithErrors } from "../../../utils/functions";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getGrantTypeConfig } from "../grantTypeConfig";

const AdministrativeInfo = ({
  grantId,
  grantData,
  onGrantCreated,
  onNext,
  isEditMode,
  showNextOnly,
  onStoreFormData,
  onValidationChange,
  initialData,
  onGrantUpdated,
  grantType = "sme",
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cfg = getGrantTypeConfig(grantType);

  const [loading, setLoading] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [ucs, setUcs] = useState([]);
  const [cycles, setCycles] = useState([]);

  const validationSchema = Yup.object({
    cycle_id: Yup.string().required("Cycle is required"),
    district_id: Yup.string().required("District is required"),
    tehsil_id: Yup.string(),
    // UC optional
    uc_name: Yup.string(),
    village_name: Yup.string(),

    latitude: Yup.number()
      .transform((value, original) => (original === "" ? null : value))
      .nullable()
      .min(-90, "Invalid latitude")
      .max(90, "Invalid latitude"),
    longitude: Yup.number()
      .transform((value, original) => (original === "" ? null : value))
      .nullable()
      .min(-180, "Invalid longitude")
      .max(180, "Invalid longitude"),
  });

  const formik = useFormik({
    initialValues: {
      cycle_id: initialData?.cycle_id || grantData?.cycle_id || "",
      district_id: initialData?.district_id || grantData?.district_id || "",
      tehsil_id: initialData?.tehsil_id || grantData?.tehsil_id || "",
      uc_name: initialData?.uc_name || grantData?.uc_name || "",
      village_name: initialData?.village_name || grantData?.village_name || "",
      latitude: initialData?.latitude || grantData?.latitude || "",
      longitude: initialData?.longitude || grantData?.longitude || "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      await handleFormSubmit(values);
    },
  });

  const handleFormSubmit = async (values) => {
    if (loading) return;
    setLoading(true);
    try {
      if (isEditMode) {
        const response = await fetchData(
          `${cfg.apiBase}/${grantId}`,
          "PUT",
          values,
          {},
          {},
          navigate,
          "",
          dispatch
        );

        if (response) {
          toast.success("Administrative information updated successfully");
          onGrantUpdated && onGrantUpdated();
        }
      } else {
        onStoreFormData && onStoreFormData(values);
        toast.success("Administrative information saved locally");
        onNext && onNext();
      }
    } catch (error) {
      console.error("Error saving administrative info:", error);
      toast.error("Failed to save administrative information");
    } finally {
      setLoading(false);
    }
  };

  const handleNextClick = () => {
    if (loading) return;
    formik.validateForm().then((validationErrors) => {
      if (Object.keys(validationErrors).length > 0) {
        formik.setTouched(
          Object.keys(formik.values).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {})
        );
        return;
      }
      proceedWithNavigation();
    });
  };

  const proceedWithNavigation = () => {
    onStoreFormData && onStoreFormData(formik.values);
    toast.success("Administrative information saved");
    onNext && onNext();
  };

  useEffect(() => {
    if (onValidationChange) {
      const timeoutId = setTimeout(() => {
        const errors = formik.validateForm();
        if (typeof errors === "object" && errors.then) {
          errors.then((validationErrors) => {
            onValidationChange(Object.keys(validationErrors).length === 0);
          });
        } else {
          onValidationChange(Object.keys(errors).length === 0);
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [formik.values, onValidationChange]);

  useEffect(() => {
    if (onStoreFormData && formik.values && !isEditMode) {
      const timeoutId = setTimeout(() => {
        onStoreFormData(formik.values);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [formik.values, onStoreFormData, isEditMode]);

  // Fetch districts
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const response = await fetchData(
          "districts",
          "GET",
          null,
          {},
          { status: 1 },
          navigate,
          "",
          dispatch
        );
        setDistricts(response?.data || []);
      } catch (error) {
        console.error("Error fetching districts:", error);
      }
    };
    fetchDistricts();
  }, []);

  // Fetch tehsils whenever district changes
  useEffect(() => {
    if (formik.values.district_id) {
      fetchTehsilsByDistrict(formik.values.district_id);
    } else {
      setTehsils([]);
      setUcs([]);
    }
  }, [formik.values.district_id]);

  useEffect(() => {
    const fetchCycles = async () => {
      try {
        const response = await fetchData(
          "cycle",
          "GET",
          null,
          {},
          { order: "ASC", sortBy: "id" },
          navigate,
          "",
          dispatch
        );
        setCycles(response?.data || []);
      } catch (error) {
        console.error("Error fetching cycles:", error);
      }
    };

    fetchCycles();
  }, [navigate, dispatch]);

  const fetchTehsilsByDistrict = async (districtId) => {
    try {
      const response = await fetchData(
        `tehsils?district_id=${districtId}`,
        "GET",
        null,
        {},
        { status: 1 },
        navigate,
        "",
        dispatch
      );
      setTehsils(response?.data || []);
    } catch (error) {
      console.error("Error fetching tehsils:", error);
      setTehsils([]);
    }
  };

  const handleDistrictChange = (value) => {
    formik.setValues((prev) => ({
      ...prev,
      district_id: value,
      tehsil_id: "",
    }));
  };

  const handleTehsilChange = (value) => {
    formik.setFieldValue("tehsil_id", value);
  };

  const handleUcNameChange = (e) => {
    formik.setFieldValue("uc_name", e.target.value);
  };

  const handleVillageNameChange = (e) => {
    formik.setFieldValue("village_name", e.target.value);
  };

  return (
    <form onSubmit={submitFormWithErrors(formik)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <ThinDropdownWithSearch
            label="Cycle"
            options={cycles.map((cycle) => ({
              value: cycle.id || cycle.cycle_id,
              label: cycle.cycle_name || cycle.cycleName || `Cycle ${cycle.id || cycle.cycle_id}`,
            }))}
            value={formik.values.cycle_id}
            onChange={(value) => formik.setFieldValue("cycle_id", value)}
            error={formik.touched.cycle_id && formik.errors.cycle_id}
          />
          {formik.touched.cycle_id && formik.errors.cycle_id && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.cycle_id}
            </div>
          )}
        </div>

        <div>
          <ThinDropdownWithSearch
            label="District"
            options={districts.map((d) => ({
              value: d.district_id,
              label: d.district_name,
            }))}
            value={formik.values.district_id}
            onChange={handleDistrictChange}
            error={formik.touched.district_id && formik.errors.district_id}
          />
          {formik.touched.district_id && formik.errors.district_id && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.district_id}
            </div>
          )}
        </div>

        <div>
          <ThinDropdownWithSearch
            label="Tehsil"
            options={tehsils.map((t) => ({
              value: t.tehsil_id,
              label: t.tehsil_name,
            }))}
            value={formik.values.tehsil_id}
            onChange={handleTehsilChange}
            disabled={!formik.values.district_id}
            error={formik.touched.tehsil_id && formik.errors.tehsil_id}
            required={false}
          />
          {formik.touched.tehsil_id && formik.errors.tehsil_id && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.tehsil_id}
            </div>
          )}
        </div>

        <div>
          <ThinTextField
            label="UC"
            name="uc_name"
            value={formik.values.uc_name}
            isRequired = {false}
            onChange={handleUcNameChange}
            onBlur={formik.handleBlur}
            error={formik.touched.uc_name && formik.errors.uc_name}
            placeholder="Enter UC name (optional)"
          />
        </div>

        <div>
          <ThinTextField
            label="Village Name"
            name="village_name"
            value={formik.values.village_name}
            onChange={handleVillageNameChange}
            onBlur={formik.handleBlur}
            error={formik.touched.village_name && formik.errors.village_name}
            placeholder="Enter village name"
            isRequired={false}
          />
          {formik.touched.village_name && formik.errors.village_name && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.village_name}
            </div>
          )}
        </div>

        <div>
          <ThinTextField
            label="Latitude"
            name="latitude"
            isRequired={false}
            type="number"
            step="0.000001"
            value={formik.values.latitude}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.latitude && formik.errors.latitude}
            placeholder="Enter latitude (e.g., 24.123456)"
          />
          {formik.touched.latitude && formik.errors.latitude && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.latitude}
            </div>
          )}
        </div>

        <div>
          <ThinTextField
            label="Longitude"
            name="longitude"
            isRequired={false}
            type="number"
            step="0.000001"
            value={formik.values.longitude}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.longitude && formik.errors.longitude}
            placeholder="Enter longitude (e.g., 67.123456)"
          />
          {formik.touched.longitude && formik.errors.longitude && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.longitude}
            </div>
          )}
        </div>
      </div>

      <div className="pt-4 flex justify-end gap-3">
        {showNextOnly && !isEditMode && (
          <button
            type="button"
            onClick={handleNextClick}
            className="bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Processing..." : "Next"}
          </button>
        )}
        {isEditMode && (
          <AddButton
            text="Update"
            type="submit"
            isLoading={loading}
            disabled={loading}
          />
        )}
      </div>
    </form>
  );
};

export default AdministrativeInfo;
